import { DynamicTariffProps } from 'features/dynamic-tariff/types'
import { useTranslation } from 'next-i18next/pages'
import { startSimulationOrRegistration } from 'utils/customerFlow'
import classNames from 'classnames'
import styles from 'features/dynamic-tariff/DynamicTariff.module.scss'
import { Button, Header, Heading, Icon, Img, Testimonials, SlideType } from '@boltenergy-be/design-system'
import Quiz from 'features/dynamic-tariff/components/quiz/Quiz'
import { ACTIVATE_IN_PORTAL_URL, QUIZ_FLOW, QUIZ_QUESTION_CARDS, QUIZ_RESULT_CARDS } from 'features/dynamic-tariff/DynamicTariff.constants'
import { DynamicPriceQuizResults } from 'features/dynamic-tariff/components/quiz/types'
import { CmsTestimonial } from 'types/cms/collection-types'
import FaqWithDefaultDescription from 'components/faq-with-default-description/FaqWithDefaultDescription'
import { useRouter } from 'next/router'

const DynamicTariff = ({ faqCategories = [], testimonials }: DynamicTariffProps) => {
  // i18n
  const { t } = useTranslation('dynamicTariff')

  // Router
  const router = useRouter()
  const { locale } = router

  return (
    <main>
      <Header title={t('hero.title')} docked color="accent-soft">
        <p>{t('hero.description')}</p>
        <div className={styles['button-group']}>
          <Button isFullwidthMobile onClick={() => startSimulationOrRegistration()} variant="layered">
            {t('hero.button.simulate')}
          </Button>
          <a href={ACTIVATE_IN_PORTAL_URL} rel="noreferrer noopener" target="_blank">
            {t('hero.button.activate')}
          </a>
        </div>
      </Header>

      <section className={classNames('section container', styles['summary-section'])}>
        <div className={styles.summary}>
          <Heading as="h2" variant="h3">
            {t('summary.title')}
          </Heading>

          <ul role="list">
            <li>
              <Icon name="badgeCheck" />
              {t('summary.advantage1')}
            </li>
            <li>
              <Icon name="badgeCheck" />
              {t('summary.advantage2')}
            </li>
            <li>
              <Icon name="badgeCheck" />
              {t('summary.advantage3')}
            </li>
            <li>
              <Icon name="badgeCheck" />
              {t('summary.advantage4')}
            </li>
          </ul>
        </div>
      </section>

      <section className={classNames('section container', styles.info)}>
        <div>
          <Heading as="h3" variant="h5" className="mb-300">
            {t('info.smartEnergy.title')}
          </Heading>
          <p>{t('info.smartEnergy.description')}</p>

          <Heading as="h3" variant="h5" className="mb-300 mt-600">
            {t('info.optimize.title')}
          </Heading>
          <p>{t('info.optimize.description')}</p>
        </div>

        <Img
          publicId="website/producers/gumption"
          className={styles['info-producer']}
          width={472}
          height={500}
          sizes="(min-width: 920px) 100vw, 33vw"
          alt=""
        />
      </section>

      <section className={classNames('section bg-lightblue', styles['quiz-section'])} id="quiz">
        <div className="container">
          <header>
            <Heading as="h2" variant="h3" className="mb-300">
              {t('quiz.title')}
            </Heading>
            <p>{t('quiz.description')}</p>
          </header>

          <div className={styles.quiz}>
            <Quiz
              resultCards={QUIZ_RESULT_CARDS}
              questionCards={QUIZ_QUESTION_CARDS}
              quizFlow={QUIZ_FLOW}
              defaultResultCard={DynamicPriceQuizResults.UNAVAILABLE_GENERAL}
              namespace="dynamicTariff"
            />
          </div>
        </div>
      </section>

      <section className={classNames('section container', styles.portal)}>
        <div>
          <Heading as="h2" variant="h3" className="mb-300">
            {t('portal.title')}
          </Heading>
          <p>{t('portal.description')}</p>
        </div>

        <Img publicId={`website/dynamic-pricing/mobile-app-mockup-${locale}`} width={551} height={551} alt="My Bolt" />
      </section>

      {!!testimonials && (
        <Testimonials
          color="accent-vibrant"
          entries={testimonials.map((testimonial: CmsTestimonial): SlideType => ({
            id: testimonial.id.toString(),
            img: {
              publicId: testimonial.image.provider_metadata.public_id,
              alt: ''
            },
            quote: testimonial.text,
            caption: {
              author: testimonial.caption.author,
              extra: testimonial.caption.extra
            }
          }))}
        />
      )}

      <FaqWithDefaultDescription categories={faqCategories} title={t('faq.title')} />
    </main>
  )
}

export default DynamicTariff
